import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { messages } from '../../../i18n';

const API_BASEURL_POINT_SETTINGS = '/api/v2/point-settings';
const API_BASEURL_POINT_LABELS = '/api/v2/point-labels';

const axiosClient = axios.create({
	withCredentials: true,
});

/**
 * Fetches the point settings for a given scope.
 *
 * Currently pointsEnabled is the only setting so this is exported from the hook directly.
 *
 * @param {Object} options                   The set of options for the request.
 * @param {String} options.scope             The scope for the request (required).
 * @param {String} options.scope_id          The scope id for the request (required).
 *
 * @returns {Object}   result
 * @returns {Object}   result.pointSettings   An object containing all the point settings.
 * @returns {Boolean}  result.pointsEnabled   A boolean indicating if points are enabled.
 */
const usePointSettings = (options = {}) => {

	const [pointSettings, setPointSettings] = useState();
	const [pointsEnabled, setPointsEnabled] = useState();
	const [pointLabel, setPointLabel] = useState();
	const [error, setError] = useState(null);

	/**
	 * Fetch point settings data
	 */
	useEffect(() => {

		const params = {
			scope: options.scope,
			scope_id: options.scope_id,
		};

		if (!options.scope || !options.scope_id) { return; }

		axiosClient.get(API_BASEURL_POINT_SETTINGS, { params }).then((response) => {

			setPointSettings(response.data && response.data[0]);
			setPointsEnabled(response.data && response.data[0] && response.data[0].points_enabled);

		}).catch((axiosError) => {

			// A 403 (not authorized for the scope) or a 404 (no point settings
			// configured for the scope — the API's documented "points not set
			// up" response, and the normal state for most organizations) both
			// just mean points are unavailable here. Expected conditions, not
			// failures: disable quietly without logging.
			const status = axiosError.response && axiosError.response.status;

			if (status === 403 || status === 404) {
				setPointsEnabled(false);
				return;
			}

			setError({ message: messages.SETTINGS_POINT_SETTINGS_LOADING_ERROR });

			console.error(axiosError);

		});

	}, [options.scope_id, options.scope]);

	/**
	 * Fetch the point label for the organization
	 */
	useEffect(() => {

		// Do not fetch point information if points are disabled for the organization
		if (!pointsEnabled) { return; }

		axiosClient.get(API_BASEURL_POINT_LABELS, {
			params: {
				scope: options.scope,
				scope_id: options.scope_id,
			}
		}).then(({ data }) => {
			if (data && data.length === 1) {
				setPointLabel(data[0]);
			}

		});
	}, [pointsEnabled, options.scope, options.scope_id]);

	return {
		pointSettings,
		pointsEnabled,
		pointLabel,
		error,
	};

};

export default usePointSettings;
